import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env, storageKeys } from '@/config/env';

/**
 * The API wraps every response in `{ data, error }` — `error` is a human-readable
 * string and arrives with HTTP 200, so a 2xx does not mean success. `unwrap()`
 * below is the only correct way to read a response body.
 */
export interface ApiEnvelope<T> {
  data: T | null;
  error: string | null;
  /**
   * `CoreBaseResponse.message`. Usually chatter, but a handful of endpoints
   * answer here instead of in `data` — the manager and login codes among
   * them — so it is part of the shape, not an extra.
   */
  message?: string | null;
  success?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Called when the server rejects our token; wired up by the auth store. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

function attachInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(storageKeys.token);
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    const language = localStorage.getItem(storageKeys.language) ?? env.defaultLanguage;
    config.headers.set('Accept-Language', `${language}-US`);
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      // `businesses/assigned` legitimately 401s for accounts with no assigned
      // business; signing the user out on it would make those accounts unusable.
      const url: string = error?.config?.url ?? '';
      if (status === 401 && !url.includes('businesses/assigned')) {
        onUnauthorized?.();
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

/** Main admin API. */
export const api = attachInterceptors(
  axios.create({
    baseURL: env.apiUrl,
    timeout: 120_000,
    headers: {
      'Content-Type': 'application/json',
      ...(env.apiToken && { 'Ocp-Apim-Subscription-Key': env.apiToken }),
      ...(env.utilityKey && { 'x-api-utility-key': env.utilityKey }),
      ...(env.paymentsKey && { 'x-api-payments-key': env.paymentsKey }),
    },
  })
);

/** Booking service — separate host, separate key. */
export const bookingApi = attachInterceptors(
  axios.create({
    baseURL: env.bookingApiUrl || env.apiUrl,
    timeout: 120_000,
    headers: {
      'Content-Type': 'application/json',
      ...(env.bookingApiKey && { 'Ocp-Apim-Subscription-Key': env.bookingApiKey }),
    },
  })
);

/** Images service. */
export const imagesApi = attachInterceptors(
  axios.create({
    baseURL: env.imagesUrl || env.apiUrl,
    timeout: 120_000,
    headers: {
      ...(env.imagesKey && { 'Ocp-Apim-Subscription-Key': env.imagesKey }),
    },
  })
);

/**
 * Read a response body, turning both transport failures and in-band failures
 * into a thrown ApiError.
 *
 * **`success` decides, and nothing else.** Every response carries it. A null
 * `data` is not a failure — it is the normal answer from every update,
 * assign, archive and delete that has nothing to hand back — and an empty
 * `message` is not a failure either. Reading either of those as the verdict is
 * how a successful save came to report "that did not go through".
 *
 * `error` and `message` are only consulted once `success` has already said no,
 * to say *why*; both may be empty, so there is a fallback.
 *
 * The signature still promises `T` rather than `T | null`: a read whose
 * endpoint answers no payload is an API fault, and letting it surface where
 * the data is used says more than a generic message thrown from in here.
 */
export async function unwrap<T>(
  request: Promise<{ data: ApiEnvelope<T> }>
): Promise<T> {
  try {
    const response = await request;
    const body = response.data;
    if (body?.success === false) throw new ApiError(reasonFrom(body));
    return body?.data as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const body = error.response?.data as ApiEnvelope<unknown> | undefined;
      throw new ApiError(reasonFrom(body) || error.message, status);
    }
    throw error;
  }
}

/** Why a request was refused. Either field may be blank, so neither is relied on. */
function reasonFrom(body: ApiEnvelope<unknown> | undefined): string {
  return body?.error || body?.message || 'The request was not accepted.';
}

export const get = <T>(url: string, config?: AxiosRequestConfig) =>
  unwrap<T>(api.get<ApiEnvelope<T>>(url, config));

export const post = <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
  unwrap<T>(api.post<ApiEnvelope<T>>(url, body, config));

export const put = <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
  unwrap<T>(api.put<ApiEnvelope<T>>(url, body, config));

export const del = <T>(url: string, config?: AxiosRequestConfig) =>
  unwrap<T>(api.delete<ApiEnvelope<T>>(url, config));
